<template>
  <div class="card">
    <CardHeader :icon="icon" :title="title" :is-small="isTitleSmall">
      <template #action-button>
        <ButtonItem
          v-if="showEditButton && !isVerifiedStatus"
          theme="neutral"
          icon-position="icon-only"
          left-icon="Edit"
          size="small"
          :is-disabled="editDisabled || isCardBeingEdited"
          @click="handleEditMode"
        />
      </template>
    </CardHeader>
    <div class="content">
      <div v-if="loading" class="loading">
        <LoaderItem />
      </div>
      <slot v-else name="content" />
    </div>
    <div v-if="editMode && !loading && showFooter" class="card-footer">
      <ButtonItem
        :text="t('common.cancel')"
        theme="neutral"
        :is-disabled="isCancelDisabled"
        @click="handleEditMode"
      />
      <ButtonItem
        :text="t('common.save')"
        theme="primary"
        :is-disabled="isSubmitDisabled"
        :is-loading="isSubmitLoading"
        @click="editWallet"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ButtonItem, CardHeader, LoaderItem } from '@lw-design-system/ui';
import { useAddWalletStore } from '~/stores/wallets/add-wallet-store';
import { useWalletInformationStore } from '~/stores/wallets/wallet-information-store';
import { useWalletsStore } from '~/stores/wallets/wallets-store';

const { t, locale, getLocaleCookie, setLocale, setLocaleCookie } = useI18n();
const { userInfos } = storeToRefs(useWalletsStore());
const { init } = useAddWalletStore();
const { isCardBeingEdited } = storeToRefs(useWalletInformationStore());

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    required: true
  },
  editDisabled: {
    type: Boolean,
    default: false
  },
  isSubmitDisabled: {
    type: Boolean,
    default: false
  },
  isCancelDisabled: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  isTitleSmall: {
    type: Boolean,
    default: false
  },
  showEditButton: {
    type: Boolean,
    default: true
  },
  isSubmitLoading: {
    type: Boolean,
    default: false
  },
  isVerifiedStatus: {
    type: Boolean,
    default: false
  },
  isWalletInformation: {
    type: Boolean,
    default: true
  },
  showFooter: {
    type: Boolean,
    default: true
  },
  isWalletCard: {
    type: Boolean,
    default: true
  }
});

const editMode = ref(false);

const emit = defineEmits<{
  (e: 'update-edit', editValue: boolean): void;
  (e: 'handle-update'): void;
}>();

onMounted(() => {
  const cookie = getLocaleCookie();
  if (cookie && isSupportedLocale(cookie)) {
    setLocale(cookie);
  }
  if (props.isWalletCard) {
    isCardBeingEdited.value = false;
  }
});

const keypressListner = async (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    await emit('handle-update');
    emit('update-edit', editMode.value);
    window.removeEventListener('keydown', keypressListner);
  }
};

const handleEditMode = () => {
  if (props.isWalletInformation) init(userInfos.value, locale.value);
  editMode.value = !editMode.value;
  emit('update-edit', editMode.value);
  if (props.isWalletCard) {
    isCardBeingEdited.value = editMode.value;
  }
  if (editMode.value) {
    window.addEventListener('keydown', keypressListner);
  }
};

const editWallet = async () => {
  await emit('handle-update');
  emit('update-edit', editMode.value);
};

watch(locale, async (selectedLocale: string) => {
  if (!isSupportedLocale(selectedLocale)) return;
  await setLocale(selectedLocale);
  setLocaleCookie(selectedLocale);
});

watch(
  () => props.editDisabled,
  (newVal) => {
    editMode.value = newVal;
  }
);
</script>

<style scoped>
.card {
  display: flex;
  flex-direction: column;
  background-color: var(--card-background);
  border-radius: 0.5rem;
  box-shadow: var(--box-shadow-card);
  flex: 1;
  height: 100%;
}

.content {
  padding: 1.5rem;
  overflow-y: hidden;
}

.card-footer {
  display: flex;
  justify-content: space-between;
  border-top: 0.0625rem solid var(--ui-separator);
  padding: 1.5rem;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 21.875rem;
}
</style>
